/*
 * GORUCK customization (Nov 2026): Free gift meter.
 * Loaded by snippets/gift-meter.liquid, only while Theme settings > Free gift meter is on.
 *
 * <gift-meter> draws the meter from the numbers Liquid rendered into its data attributes. GiftMeter (one per page,
 * however many cart views there are) keeps the cart in line with the rules:
 *   - gear >= threshold, no gift line, not declined, variant available -> add the gift (quantity 1, _gift property)
 *   - gear < threshold and a gift line -> remove it (and set _gift_dropped for the "get it back" copy)
 *   - more than one gift line, or a quantity above 1 -> back to one line of 1
 *   - the shopper's Remove on the gift line -> remove it and set _gift_declined so it isn't re-added
 *   - "Add my free ..." -> clear _gift_declined and add it
 * After a change it re-renders the cart the way Taiga does (bundled section rendering of main-cart, handed to
 * <cart-items>.updateCartSections) and fires cart:update so the header count follows.
 *
 * Guards against loops and double adds:
 *   - one request at a time; triggers that land meanwhile queue a single re-check
 *   - every decision is re-checked against a fresh /cart.js before anything is sent
 *   - it waits while Taiga's own quantity change is pending or in flight (new lines go to the top of the cart,
 *     so adding the gift renumbers the lines Taiga's change request points at)
 *   - clicks on the cart and the checkout button are held while it works
 *   - a circuit breaker stops automatic adds for the rest of the page view after 4 in a minute (a loop needs
 *     repeated adds; removals can't repeat on their own). The shopper then gets the "Add my free ..." button.
 */
(() => {
  if (window.GiftMeter) return;

  const SECTION_ID = 'main-cart';

  const GiftMeter = {
    busy: false,
    queued: false,
    fresh: false,
    timer: null,
    lastInput: 0,
    adds: [],
    stopped: false,
    addFailed: false,
    lastSync: '',
    dispatching: false,

    element() {
      return document.querySelector('gift-meter');
    },

    rate() {
      const rate = parseFloat(window.Shopify && window.Shopify.currency && window.Shopify.currency.rate);
      return rate > 0 ? rate : 1;
    },

    /**
     * The threshold is set in the shop currency (USD). Carts in another currency compare against the converted amount.
     * @param {number} shopCents
     * @returns {number} cents in the cart's currency
     */
    threshold(shopCents) {
      return Math.round(shopCents * this.rate());
    },

    config(el) {
      return {
        variantId: parseInt(el.dataset.variantId, 10),
        available: el.dataset.available === 'true',
        threshold: this.threshold(parseInt(el.dataset.threshold, 10) || 0),
        excluded: el.dataset.excluded || '|',
        label: el.dataset.giftLabel || 'Free gift',
      };
    },

    /**
     * What Liquid saw when it rendered the meter.
     * @returns {{gear: number, lines: Array<{key: string, quantity: number}>, declined: boolean}}
     */
    fromElement(el) {
      const lines = (el.dataset.giftLines || '').split('|').filter(Boolean).map((pair) => {
        const at = pair.lastIndexOf('=');
        return { key: pair.slice(0, at), quantity: parseInt(pair.slice(at + 1), 10) || 0 };
      });
      return { gear: parseInt(el.dataset.gear, 10) || 0, lines, declined: el.dataset.declined === 'true' };
    },

    /**
     * The same numbers from /cart.js. Keep in step with the Liquid in snippets/gift-meter.liquid.
     */
    fromCart(cart, cfg) {
      let gear = 0;
      const lines = [];
      (cart.items || []).forEach((item) => {
        if (item.properties && item.properties._gift) {
          lines.push({ key: item.key, quantity: item.quantity });
          return;
        }
        const type = `|${String(item.product_type || '').trim().toLowerCase()}|`;
        if (type !== '||' && cfg.excluded.includes(type)) return;
        // A paid line of the gift variant counts at its undiscounted price (see snippets/gift-meter.liquid).
        gear += item.variant_id === cfg.variantId ? item.original_line_price : item.final_line_price;
      });
      const attributes = cart.attributes || {};
      return { gear, lines, declined: Boolean(attributes._gift_declined) };
    },

    /**
     * What has to change so the cart follows the rules. Never both an update and an add.
     */
    plan(state, cfg) {
      const updates = {};
      const attributes = {};
      let add = false;
      if (state.gear < cfg.threshold) {
        if (state.lines.length) {
          state.lines.forEach((line) => { updates[line.key] = 0; });
          attributes._gift_dropped = 'true';
        }
      } else if (state.lines.length) {
        state.lines.forEach((line, index) => {
          if (index > 0) updates[line.key] = 0;
          else if (line.quantity !== 1) updates[line.key] = 1;
        });
      } else {
        add = !state.declined && cfg.available && !this.addFailed && !this.stopped;
      }
      return { updates, attributes, add };
    },

    hasWork(plan) {
      return plan.add || Object.keys(plan.updates).length > 0;
    },

    /**
     * @param {number} delay
     * @param {boolean} fresh - true when a cart event may have left the rendered numbers stale
     */
    schedule(delay = 250, fresh = false) {
      if (fresh) this.fresh = true;
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.check(), delay);
    },

    // Taiga waits 300ms after the last quantity click before it sends the change, then marks <cart-items> while it runs.
    themeBusy() {
      if (Date.now() - this.lastInput < 800) return true;
      return Array.from(document.querySelectorAll('cart-items')).some((el) => el.classList.contains('is-processing'));
    },

    async check() {
      if (this.busy) {
        this.queued = true;
        return;
      }
      if (this.themeBusy()) {
        this.schedule(400);
        return;
      }
      const el = this.element();
      if (!el) return;
      const cfg = this.config(el);
      const rendered = this.fromElement(el);
      const fresh = this.fresh;
      this.fresh = false;
      // A settled cart costs no extra request: only ask the server when the rendered numbers call for a change
      // or a cart event may have left them stale.
      if (!fresh && !this.hasWork(this.plan(rendered, cfg))) return;

      await this.run(async () => {
        const cart = await this.request('get');
        const state = this.fromCart(cart, cfg);
        const plan = this.plan(state, cfg);
        if (!this.hasWork(plan)) {
          this.syncView(rendered, state);
          return;
        }
        let response;
        if (plan.add) {
          if (!this.allowAdd()) return;
          try {
            response = await this.request('add', { items: [this.giftItem(cfg)] });
          } catch (error) {
            // For example it sold out since the page loaded. Leave the button for the shopper instead of retrying.
            this.addFailed = true;
            throw error;
          }
        } else {
          const body = { updates: plan.updates };
          if (Object.keys(plan.attributes).length) body.attributes = plan.attributes;
          response = await this.request('update', body);
        }
        this.refresh(response);
      });
    },

    giftItem(cfg) {
      return { id: cfg.variantId, quantity: 1, properties: { _gift: cfg.label } };
    },

    /**
     * The shopper removed the gift line. Remember it so the gift isn't added back.
     * @param {string} key - line item key
     */
    async decline(key) {
      if (this.busy || !key) return;
      await this.run(async () => {
        const response = await this.request('update', { updates: { [key]: 0 }, attributes: { _gift_declined: 'true' } });
        this.refresh(response);
      });
    },

    // "Add my free ..." in the waiting state.
    async accept() {
      if (this.busy) return;
      const el = this.element();
      if (!el) return;
      const cfg = this.config(el);
      this.addFailed = false;
      await this.run(async () => {
        // A blank value deletes the attribute.
        let response = await this.request('update', { attributes: { _gift_declined: '' } });
        const state = this.fromCart(response, cfg);
        if (!state.lines.length && state.gear >= cfg.threshold && cfg.available) {
          try {
            response = await this.request('add', { items: [this.giftItem(cfg)] });
          } catch (error) {
            this.addFailed = true;
            console.error(error);
          }
        }
        this.refresh(response);
      });
    },

    // Nothing to change, but a slower render Taiga started earlier (e.g. on page load) may have painted an older
    // cart over ours. Re-render once per cart state when what's shown doesn't match the cart.
    syncView(rendered, state) {
      const actual = JSON.stringify(state);
      if (JSON.stringify(rendered) === actual || this.lastSync === actual) return;
      this.lastSync = actual;
      const cartItems = document.querySelector('cart-items');
      if (cartItems && typeof cartItems.renderItems === 'function') cartItems.renderItems();
    },

    // Circuit breaker for automatic adds.
    allowAdd() {
      const now = Date.now();
      this.adds = this.adds.filter((time) => now - time < 60000);
      if (this.adds.length >= 4) {
        this.stopped = true;
        console.warn('Free gift meter: the gift was added automatically 4 times in a minute, so it stopped adding it on this page view.');
        return false;
      }
      this.adds.push(now);
      return true;
    },

    async run(task) {
      this.busy = true;
      this.lock(true);
      try {
        await task();
      } catch (error) {
        console.error(error);
      } finally {
        this.busy = false;
        this.lock(false);
        this.redraw();
        if (this.queued) {
          this.queued = false;
          this.schedule(100, true);
        }
      }
    },

    lock(on) {
      document.querySelectorAll('cart-items').forEach((el) => el.classList.toggle('gift-meter-busy', on));
      document.querySelectorAll('button[name="checkout"]').forEach((button) => {
        if (on && !button.disabled) {
          button.disabled = true;
          button.dataset.giftMeterLocked = '';
        } else if (!on && 'giftMeterLocked' in button.dataset) {
          button.disabled = false;
          delete button.dataset.giftMeterLocked;
        }
      });
    },

    redraw() {
      document.querySelectorAll('gift-meter').forEach((el) => {
        if (typeof el.draw === 'function') el.draw();
      });
    },

    /**
     * @param {'get'|'add'|'update'} action
     * @param {Object} body
     */
    async request(action, body) {
      if (action === 'get') {
        const res = await fetch(`${window.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
        if (!res.ok) throw new Error(`Free gift meter: cart request failed (${res.status})`);
        return res.json();
      }
      const res = await fetch(`${window.routes[`cart_${action}_url`]}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(Object.assign({}, body, { sections: SECTION_ID, sections_url: window.location.pathname })),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(`Free gift meter: ${json.description || json.message || `cart ${action} failed (${res.status})`}`);
      return json;
    },

    // Re-render the drawer or cart page the way Taiga's own quantity changes do.
    refresh(response) {
      const html = response && response.sections && response.sections[SECTION_ID];
      const cartItems = document.querySelector('cart-items');
      if (html && cartItems && typeof cartItems.updateCartSections === 'function') {
        cartItems.updateCartSections(new DOMParser().parseFromString(html, 'text/html'));
      } else if (cartItems && typeof cartItems.renderItems === 'function') {
        cartItems.renderItems();
      }
      // Taiga's header count listens for this and reads the cart itself. An empty detail keeps
      // cart-recommendations from treating the gift as the first product.
      this.dispatching = true;
      document.dispatchEvent(new CustomEvent('cart:update', { detail: {} }));
      this.dispatching = false;
    },
  };

  class GiftMeterElement extends HTMLElement {
    connectedCallback() {
      this.draw();
      GiftMeter.schedule();
    }

    draw() {
      const data = this.dataset;
      if (data.available !== 'true') {
        this.hidden = true;
        return;
      }
      const threshold = GiftMeter.threshold(parseInt(data.threshold, 10) || 0);
      const gear = parseInt(data.gear, 10) || 0;
      const hasGift = Boolean(data.giftLines);

      let state;
      if (gear < threshold) state = data.dropped === 'true' ? 'dropped' : 'under';
      else if (hasGift && data.giftFree === 'true') state = 'in-cart';
      else if (hasGift) state = data.hasCode === 'true' ? 'code' : 'paid';
      else if (data.declined === 'true' || GiftMeter.addFailed || GiftMeter.stopped) state = 'waiting';
      else state = 'adding';
      this.dataset.state = state;

      this.querySelectorAll('[data-gm-when]').forEach((node) => {
        node.hidden = !node.dataset.gmWhen.split(' ').includes(state);
      });

      const gearText = this.money(Math.floor(gear / 100));
      const thresholdText = this.money(Math.round(threshold / 100));
      this.fill('[data-gm-gap]', this.money(Math.ceil(Math.max(threshold - gear, 0) / 100)));
      this.fill('[data-gm-gear]', gearText);
      this.fill('[data-gm-threshold]', thresholdText);

      const bar = this.querySelector('[role="progressbar"]');
      if (bar) {
        const max = Math.round(threshold / 100);
        bar.setAttribute('aria-valuemax', max);
        bar.setAttribute('aria-valuenow', Math.min(Math.floor(gear / 100), max));
        bar.setAttribute('aria-valuetext', `${gearText} of ${thresholdText} in gear`);
        const percent = threshold > 0 ? Math.min(100, Math.max(0, (gear / threshold) * 100)) : 100;
        const fill = bar.querySelector('.gift-meter__fill');
        if (fill) fill.style.width = `${percent}%`;
      }

      this.hidden = false;
    }

    fill(selector, text) {
      this.querySelectorAll(selector).forEach((node) => { node.textContent = text; });
    }

    // Whole units, like the mockups: "$43 of gear away", "$257 in gear", "$300".
    money(amount) {
      if (!this.formatter) {
        const options = { style: 'currency', currency: this.dataset.currency || 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 };
        try {
          this.formatter = new Intl.NumberFormat(this.dataset.locale || undefined, Object.assign({ currencyDisplay: 'narrowSymbol' }, options));
        } catch (error) {
          this.formatter = new Intl.NumberFormat(undefined, options);
        }
      }
      return this.formatter.format(amount);
    }
  }

  window.GiftMeter = GiftMeter;
  if (!customElements.get('gift-meter')) customElements.define('gift-meter', GiftMeterElement);

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const remove = event.target.closest('[data-gift-meter-remove]');
    if (remove) {
      event.preventDefault();
      GiftMeter.decline(remove.dataset.giftMeterRemove);
      return;
    }
    if (event.target.closest('[data-gift-meter-add]')) {
      event.preventDefault();
      GiftMeter.accept();
    }
  });

  // Quantity buttons fire change on the input; note it so a pending Taiga change isn't overtaken.
  document.addEventListener('change', (event) => {
    if (event.target instanceof Element && event.target.closest('cart-items')) GiftMeter.lastInput = Date.now();
  }, true);

  document.addEventListener('cart:update', () => {
    if (!GiftMeter.dispatching) GiftMeter.schedule(250, true);
  });

  // Back/forward cache: the page comes back as it was, the cart may not have.
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) GiftMeter.schedule(250, true);
  });
})();